'use client';

import React from 'react';

/*──────────────────────────────────────────────────────────────────────
 * FOOTER
 * Three-column grid: company info, description, legal links.
 * Collapses to single column on mobile via embedded <style> tag.
 *──────────────────────────────────────────────────────────────────────*/

interface LegalLink {
  label: string;
  href: string;
}

const LEGAL: LegalLink[] = [
  { label: 'Privacy Policy', href: '/legal/privacy' },
  { label: 'Terms of Service', href: '/legal/terms' },
  { label: 'Cookie Policy', href: '/legal/cookies' },
];

export function Footer() {
  return (
    <footer
      style={{
        /* Narrative arc tail — the green confidence undertone settles and fades out,
           closing the crimson→indigo→green journey. Very faint by design. */
        background:
          'radial-gradient(ellipse 120% 100% at 50% 0%, color-mix(in srgb, var(--green) 4%, transparent) 0%, transparent 55%), var(--bg-veil)',
        borderTop: '1px solid var(--border)',
        padding: '64px 24px 52px',
      }}
    >
      <div
        className="footer-grid"
        style={{
          maxWidth: '1180px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1.2fr 1fr',
          gap: '48px',
          alignItems: 'start',
        }}
      >
        {/* company */}
        <div>
          <div
            style={{
              fontSize: '15px',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '16px',
            }}
          >
            Talgrid Tech Private Limited
          </div>
          <div
            style={{
              fontSize: '13.5px',
              color: 'var(--text-2)',
              lineHeight: 1.7,
            }}
          >
            Bengaluru, India
          </div>
          <a
            href="mailto:support@vhire.com"
            style={{
              fontSize: '13.5px',
              color: 'var(--text-2)',
              textDecoration: 'none',
              transition: 'color .2s',
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.color = 'var(--text)')
            }
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.color = 'var(--text-2)')
            }
          >
            support@vhire.com
          </a>
          {/* Wayfinding back to the candidate side (vhire.com home). */}
          <a
            href="/"
            style={{
              display: 'block',
              marginTop: '18px',
              fontSize: '13.5px',
              fontWeight: 600,
              color: 'var(--headline-accent)',
              textDecoration: 'none',
            }}
          >
            For job seekers →
          </a>
        </div>

        {/* description */}
        <div>
          <p
            style={{
              fontSize: '14px',
              color: 'var(--text-2)',
              lineHeight: 1.7,
              maxWidth: '420px',
            }}
          >
            vHire is a technology platform connecting employers and recruiting
            partners to streamline the hiring process with AI-driven insights.
          </p>
        </div>

        {/* legal + copyright */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '14px',
          }}
        >
          <div
            className="footer-legal"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: '12px',
            }}
          >
            {LEGAL.map((l: LegalLink, j: number) => (
              <a
                key={j}
                href={l.href}
                style={{
                  fontSize: '13.5px',
                  color: 'var(--text-2)',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  transition: 'color .2s',
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
                  (e.currentTarget.style.color = 'var(--text)')
                }
                onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
                  (e.currentTarget.style.color = 'var(--text-2)')
                }
              >
                {l.label}
              </a>
            ))}
          </div>
          <div
            style={{
              fontSize: '12.5px',
              color: 'var(--text-3)',
              marginTop: '14px',
            }}
          >
            © 2026 Talgrid Tech Pvt Ltd. All rights reserved.
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 760px){
        .footer-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
        .footer-grid > div:last-child { align-items: flex-start !important; }
        .footer-legal { align-items: flex-start !important; }
      }`}</style>
    </footer>
  );
}

export default Footer;
